import React, { useEffect, useState } from 'react'
import HeaderDataDisplayer from './HeaderDataDisplayer'
import DataDisplayerItem from './DataDisplayerItem'
import { useDispatch } from 'react-redux'
import { addNewDispplayer, deleteSelectedItem, updateColumnItems, updateDisplayerAllItemsSelectedStatus, updateDisplayerItems, updateDisplayerLoadingStatus, updateDisplayerPagination, updateDisplayerSearch, updateDisplayerSelectedItems, useReturnSingleDispplayer } from './dataDisplyerSlice'
import DataDisplayerFilterBar from './DataDisplayerFilterBar'
import NothingFound from '../NothingFound'
import DisplayerBottomBox from './DisplayerBottomBox'
import { ISearchParam } from '../../features/BaseApp'
import PreloaderMask from '../PreloaderMask'
import { IconTypes } from '../GenerateIcon'
import { IAxiosDefaultDataObject } from '../../config'
import { IDynamicForm } from '../Form/DynamicForm'
import { updateQuickNotificationDetails } from '../../features/notifications/notificationsSlice'

/**
 * Pagination
 */
export interface IDataDisplayerPagination {
    count: number,
    current_page: number,
    total_pages: number
}

/**
 * Item Field
 */
export type DataDisplayerItemFieldTypes = "title" | "text" | "thumbnail" | "date" | "country" | "status" | "current-location" | "map"

export interface IDataDisplayerItemField {
    id: string,
    type: DataDisplayerItemFieldTypes,
    label: string,
    value: string,
    maxWidth?: number,
    minWidth?: number,
    link?: string
    quickItemView?: (item: IAxiosDefaultDataObject, shown: boolean, handleHide: () => void) => JSX.Element,
    customContent?: (item: IAxiosDefaultDataObject) => JSX.Element,
    showLabel?: boolean
}

export interface IDataDisplayerColumnField {
    id: string,
    type: DataDisplayerItemFieldTypes,
    label: string,
    maxWidth?: number,
    minWidth?: number,
}

/**
 * Item
 */
export interface IDataDisplayerItem {
    id: number,
    fields: Array<IDataDisplayerItemField>,
    rawItem: { [key: string]: any },
    subItems?: Array<IDataDisplayerItem>,
    customActionOptions?: Array<IDataDisplyerItemActionOption>,
    customHandleDeleteItem?: (id: number) => void,
}

/**
 * Items Options
 */
export interface IDataDisplyerItemActionOption {
    icon: IconTypes,
    label: string,
    onClick: (rawItem:  { [key: string]: any }) => void,
    link?: string
}

/**
 * Nothing found
 */
export interface IDataDisplayerNothingFound {
    title: string, 
    message: string 
}

/**
 * Custom Header Option Button
 */
export interface IDataDisplayerHeaderOptionActionButton {
    icon: IconTypes,
    label: string,
    onClick: () => void,
    customContent?: (button: IDataDisplayerHeaderOptionActionButton, selectedItems: number[]) => JSX.Element
}   

/**
 * Data Displayer
 */
export interface IDataDisplayerBase {
    name: string,
    items: Array<IDataDisplayerItem>,
    columnItems: Array<IDataDisplayerColumnField>,
    notionFound?: IDataDisplayerNothingFound,
    pagination: IDataDisplayerPagination,
    loading: boolean,
    loadItems: (page: number, search: string|null, disablePagination: boolean, addItems?: boolean, searchParams?: Array<ISearchParam>) => void,
    deleteItem: (id: number) => void,
    deleteManyItems: (records: Array<IAxiosDefaultDataObject>) => void,
    itemActionOptions?: Array<IDataDisplyerItemActionOption>,
    search?: string,
    searchParamsList?: ISearchParam[],
    updateSearch: (search: string) => void,
    filterForm?: IDynamicForm,
    customHeaderOptionActionButtons?: Array<IDataDisplayerHeaderOptionActionButton>,
    viewItemTemplateLink?: string,
    disableSearch?: boolean,
    disableSelect?: boolean
}

export interface IDataDisplayer extends IDataDisplayerBase {
    selectedItems: Array<number>,
    allItemsSelected?: boolean,
    hideColumnItems: Array<string>,
}

export const EmptyDataDisplyer: IDataDisplayer = {
    name: "",
    items: [],
    selectedItems: [],
    columnItems: [],
    hideColumnItems: [],
    pagination: {
        count: 0,
        current_page: 0,
        total_pages: 0
    },
    loading: false,
    loadItems: (page, search, disablePagination, addItems, searchParams) => {},
    deleteItem: (id: number) => {},
    deleteManyItems: (records) => {},
    updateSearch: (search) => {}
}

export default function DataDisplayer(props: IDataDisplayerBase) {

    const dispatch = useDispatch()

    const { name, columnItems, notionFound, pagination, loading, loadItems, itemActionOptions, deleteItem, deleteManyItems, updateSearch, search, filterForm, customHeaderOptionActionButtons } = props
    const currentDisplayer = useReturnSingleDispplayer(name)

    const [undoTimeout, setUndoTimeout] = useState<NodeJS.Timeout | null>(null)

    const items = currentDisplayer.items
    const selected = currentDisplayer.selectedItems

    const handleUpdateSelectedItems = (items: Array<number>) => dispatch(updateDisplayerSelectedItems({ displayerName: name, selectedItems: items }))

    const updateSelected = (id: number) => {
        
        let listOfSelected = [
            ...selected
        ]
        
        if (selected.includes(id)){
            listOfSelected = listOfSelected.filter(select => select !== id)
        }else{
            listOfSelected.push(id)
        }

        
        handleUpdateSelectedItems(listOfSelected)
    }

    const selectAllItems = () => {
        
        let listOfSelected: Array<number> = []

        if (currentDisplayer.allItemsSelected){
            listOfSelected = []
        }else{
            items.map((item) => {
                listOfSelected.push(item.id)
            })
        }

        dispatch(updateDisplayerAllItemsSelectedStatus({ displayerName: name, status: currentDisplayer.allItemsSelected ? false : true }))
        handleUpdateSelectedItems(listOfSelected)
    }

    /**
     * Register data displayer
     */
    useEffect(() => {

        dispatch(addNewDispplayer({
            name: name,
            search: search,
            items: [],
            selectedItems: [],
            hideColumnItems: [],
            columnItems: columnItems,
            pagination: pagination,
            loading: loading,
            loadItems: loadItems,
            deleteItem: deleteItem,
            deleteManyItems: deleteManyItems,
            updateSearch: updateSearch
        }))

    }, [])

    /**
     * Items
     */
    useEffect(() => {
        dispatch(updateDisplayerItems({ displayerName: name, items: props.items }))
    }, [props.items])

    /**
     * Pagination
     */
    useEffect(() => {
        dispatch(updateDisplayerPagination({ displayerName: name, pagination: props.pagination }))
    }, [props.pagination])

    /**
     * Update loading
     */
    useEffect(() => {
        dispatch(updateDisplayerLoadingStatus({ displayerName: name, status: props.loading }))
    }, [props.loading])

    /**
     * Update search
     */
    useEffect(() => {
        dispatch(updateDisplayerSearch({ displayerName: name, search: props.search ? props.search : "" }))
    }, [props.search])

    /**
     * Update column items
     */
    useEffect(() => {
        dispatch(updateColumnItems({ displayerName: name, columnItems: props.columnItems }))
    }, [props.columnItems])

    /**
     * update filter form
     */
    // useEffect(() => {
    //     console.log("props.filterForm", props.filterForm)
    // }, [props.filterForm])

    /**
     * Undo delete item
     */
    const undoDeleteItem = () => {
        if (undoTimeout){
            console.log("clearTimeout")
            clearTimeout(undoTimeout)
        }
    }

    /**
     * handle delete item
     */
    const handleDeleteItem = (id: number) => {

        deleteItem(id)
        dispatch(deleteSelectedItem({ displayerName: name, id: id }))
        // updateQuickNotificationDetails(true, "Item will be deleted in 5 seconds", "error", dispatch, () => {}, 5000, false, [
        //     {
        //         label: "Undo",
        //         handleClose: false,
        //         onClick: () => undoDeleteItem()
        //     }
        // ])

        // const timeout = setTimeout(() => {
        //     console.log("delete item")
        //     deleteItem(id)
        //     dispatch(deleteSelectedItem({ displayerName: name, id: id }))
        // }, 5000)

    }
    
    return(
        <div className="data-displayer-container">
            <div className="content-of-data-displayer-container">

                <PreloaderMask open={loading} />

                <DataDisplayerFilterBar hideSearch={props.disableSearch} loadItems={loadItems} displayer={currentDisplayer} handleUpdateSearch={updateSearch} handleDeleteItem={handleDeleteItem} filterForm={filterForm} customHeaderOptionActionButtons={customHeaderOptionActionButtons} />

                <div className="wrapper-of-data-displayer-container">
                    <HeaderDataDisplayer displayer={currentDisplayer} selectAllItems={selectAllItems} />

                    <div className="items-of-data-displayer-container">
                        <div className="content-of-items-of-data-displayer-container">
                            {items.map((item: IDataDisplayerItem, index: number) => <DataDisplayerItem handleDeleteItem={handleDeleteItem} itemActionOptions={itemActionOptions ? itemActionOptions : []} hideColumns={currentDisplayer.hideColumnItems} selectedList={selected} updateSelected={updateSelected} {...item} key={index} />)}
                            {/* {items.length > 0 && <div className="empty-of-items-of-data-displayer-container"></div>} */}
                        </div>
                    </div>
                </div>

                {items.length == 0 && <div className="data-displayer-noting-found"><div className="content-of-data-displayer-noting-found"><NothingFound title={notionFound?.title} message={notionFound?.message} /></div></div>}
                <DisplayerBottomBox displayer={currentDisplayer} />
            </div>
        </div>
    )
}